import "./formulaireAbsence.css";
import React, { useState, useRef, useCallback, useEffect } from "react";
import FormConfirmation from "./components/FormConfirmation";
import Select from "react-select";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import schools from "./schools.json";
import { Link } from "react-router-dom";
import RadioButton from "./components/RadioButton";
import { motion, AnimatePresence } from "framer-motion";
import EmailVerification from "./components/EmailVerification";

function CongeTraitementDiffere() {
  useEffect(() => { document.title = "SEVF | Traitement différé"; }, []);

  const [submittedData, setSubmittedData] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  const [form_Data, set_Form_Data] = useState({
    firstName: "",
    lastName: "",
    establishment: "",
    matricule: "",
    phoneNumber: "",
    contractChoice: "",
    paymentStartYear: "",
    paymentStartDay: "",
    leaveYear: "",
    leavePeriod: "",
    emailVerified: false,
    emailToVerify: "",
  });

  const initialFormData = {
    firstName: "",
    lastName: "",
    establishment: "",
    matricule: "",
    phoneNumber: "",
    contractChoice: "",
    paymentStartYear: "",
    paymentStartDay: "",
    leaveYear: "",
    leavePeriod: "",
    emailVerified: false,
    emailToVerify: "",
  };

  // Contract options
  const contractOptions = [
    {
      id: "demiAnnee2ans",
      label: "½ année / 2 ans = 75,00 %",
      isHalfYear: true,
    },
    {
      id: "demiAnnee3ans",
      label: "½ année / 3 ans = 83,34 %",
      isHalfYear: true,
    },
    {
      id: "demiAnnee4ans",
      label: "½ année / 4 ans = 87,50 %",
      isHalfYear: true,
    },
    {
      id: "demiAnnee5ans",
      label: "½ année / 5 ans = 90,00 %",
      isHalfYear: true,
    },
    { id: "unAn3ans", label: "1 an / 3 ans = 66,66 %", isHalfYear: false },
    { id: "unAn4ans", label: "1 an / 4 ans = 75,00 %", isHalfYear: false },
    { id: "unAn5ans", label: "1 an / 5 ans = 80,00 %", isHalfYear: false },
  ];

  // Generate school year options (current year + next 10 years)
  const generateSchoolYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 10; i++) {
      const startYear = currentYear + i;
      years.push({
        value: `${startYear}-${startYear + 1}`,
        label: `${startYear}-${startYear + 1}`,
      });
    }
    return years;
  };

  const schoolYearOptions = generateSchoolYears();

  // Check if selected contract is half-year type
  const isHalfYearContract = () => {
    const selected = contractOptions.find(
      (opt) => opt.id === form_Data.contractChoice,
    );
    return selected ? selected.isHalfYear : null;
  };

  const apiUrl = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formValidated, setFormValidated] = useState(false);
  const [firstFormValidation, setFirstFormValidation] = useState(false);
  const [acknowledgeSignature, setAcknowledgeSignature] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);

  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const establishmentRef = useRef(null);
  const matriculeRef = useRef(null);
  const phoneRef = useRef(null);
  const contractChoiceRef = useRef(null);
  const paymentStartYearRef = useRef(null);
  const paymentStartDayRef = useRef(null);
  const leaveYearRef = useRef(null);
  const leavePeriodRef = useRef(null);

  // Auto-set leavePeriod when contract type changes
  useEffect(() => {
    if (form_Data.contractChoice) {
      const isHalf = isHalfYearContract();
      if (isHalf === false) {
        // 1 year contract - auto-select full year
        set_Form_Data((prev) => ({
          ...prev,
          leavePeriod: "anneeScolaireComplete",
        }));
      } else if (
        isHalf === true &&
        form_Data.leavePeriod === "anneeScolaireComplete"
      ) {
        // Half year contract - reset leavePeriod if it was set to full year
        set_Form_Data((prev) => ({
          ...prev,
          leavePeriod: "",
        }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form_Data.contractChoice]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    set_Form_Data((prevData) => {
      const updatedData = { ...prevData, [name]: value };
      if (firstFormValidation) {
        setFormErrors(validateFormData(updatedData, acknowledgeSignature));
      }
      return updatedData;
    });
  };

  const handleContractChange = (event) => {
    const { value } = event.target;
    set_Form_Data((prevData) => {
      const updatedData = {
        ...prevData,
        contractChoice: value,
        leavePeriod: "", // Reset leave period when contract changes
      };
      if (firstFormValidation) {
        setFormErrors(validateFormData(updatedData, acknowledgeSignature));
      }
      return updatedData;
    });
  };

  const validateFormData = (form_Data, acknowledgeSignature) => {
    const errors = {};

    // Validate firstName
    if (!form_Data.firstName) {
      errors.firstName = "Le prénom est requis.";
    } else if (/\d/.test(form_Data.firstName)) {
      errors.firstName = "Le prénom ne doit pas contenir de chiffres.";
    }

    // Validate lastName
    if (!form_Data.lastName) {
      errors.lastName = "Le nom est requis.";
    } else if (/\d/.test(form_Data.lastName)) {
      errors.lastName = "Le nom ne doit pas contenir de chiffres.";
    }

    // Validate establishment
    if (!form_Data.establishment) {
      errors.establishment = "Le nom de l'école est requis.";
    }

    // Validate matricule
    if (!form_Data.matricule) {
      errors.matricule = "Le numéro de matricule est requis.";
    } else if (form_Data.matricule.length > 7) {
      errors.matricule =
        "Le numéro de matricule ne doit pas dépasser 7 chiffres.";
    }

    // Validate phoneNumber
    if (!form_Data.phoneNumber) {
      errors.phoneNumber = "Le numéro de téléphone est requis.";
    } else if (form_Data.phoneNumber.length !== 12) {
      errors.phoneNumber = "Le numéro de téléphone doit contenir 10 chiffres.";
    }

    // Validate contractChoice
    if (!form_Data.contractChoice) {
      errors.contractChoice = "Veuillez sélectionner un choix de contrat.";
    }

    // Validate paymentStartYear
    if (!form_Data.paymentStartYear) {
      errors.paymentStartYear =
        "Veuillez sélectionner l'année scolaire du début du paiement.";
    }

    // Validate paymentStartDay
    if (!form_Data.paymentStartDay) {
      errors.paymentStartDay =
        "Veuillez sélectionner le moment du début du paiement.";
    }

    // Validate leaveYear
    if (!form_Data.leaveYear) {
      errors.leaveYear =
        "Veuillez sélectionner l'année scolaire de votre congé.";
    }

    // Validate leavePeriod (only required for half-year contracts)
    const selectedContract = contractOptions.find(
      (opt) => opt.id === form_Data.contractChoice,
    );
    const isHalf = selectedContract ? selectedContract.isHalfYear : true;
    if (isHalf && !form_Data.leavePeriod) {
      errors.leavePeriod = "Veuillez sélectionner la période de votre congé.";
    }

    if (!acknowledgeSignature) {
      errors.acknowledgeSignature =
        "Veuillez cocher la case pour accepter les avertissements.";
    }

    if (!emailVerified) {
      errors.emailVerification = "Veuillez vérifier votre adresse e-mail.";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateFormData(form_Data, acknowledgeSignature);

    if (!firstFormValidation) {
      setFirstFormValidation(true);
    }

    if (Object.keys(errors).length === 0) {
      setFormValidated(true);
      setFormErrors({});

      try {
        setSubmitError(null);
        setLoading(true);
        const formDataToSend = new FormData();
        for (const key in form_Data) {
          if (key === "file" && form_Data.file) {
            formDataToSend.append("file", form_Data.file);
          } else {
            formDataToSend.append(key, form_Data[key]);
          }
        }

        const response = await fetch(`${apiUrl}/document/traitement-differe`, {
          method: "POST",
          body: formDataToSend,
        });
        if (!response.ok) {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            const messages = errorData.errors?.join(" ") || "Une erreur s'est produite.";
            throw new Error(messages);
          }
          throw new Error("Une erreur s'est produite.");
        }
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          await response.json();
        } else {
          await response.text();
        }

        // Capture data for confirmation screen before resetting
        const selectedContract = contractOptions.find((o) => o.id === form_Data.contractChoice);
        const captured = {
          name: `${form_Data.firstName} ${form_Data.lastName}`,
          matricule: form_Data.matricule,
          establishment: form_Data.establishment,
          contrat: selectedContract ? selectedContract.label : form_Data.contractChoice,
          anneeConge: form_Data.leaveYear || "—",
          dateSent: new Date().toLocaleString("fr-CA", { dateStyle: "long", timeStyle: "short" }),
        };

        set_Form_Data(initialFormData);
        setAcknowledgeSignature(false);
        setFirstFormValidation(false);
        setFormValidated(false);
        setFormErrors({});
        setEmailVerified(false);

        setSubmittedData(captured);
      } catch (error) {
        console.error("Error:", error);
        setSubmitError(
          error.message && error.message !== "Failed to fetch"
            ? error.message
            : "Une erreur s'est produite. Veuillez réessayer. Si le problème persiste, veuillez contacter valeriec@sevf.ca.",
        );
      } finally {
        setLoading(false);
      }
    } else {
      setFormErrors(errors);
      setFormValidated(true);

      if (errors.firstName) {
        firstNameRef.current?.scrollIntoView({ behavior: "smooth" });
      } else if (errors.lastName) {
        lastNameRef.current?.scrollIntoView({ behavior: "smooth" });
      } else if (errors.establishment) {
        establishmentRef.current?.scrollIntoView({ behavior: "smooth" });
      } else if (errors.matricule) {
        matriculeRef.current?.scrollIntoView({ behavior: "smooth" });
      } else if (errors.phoneNumber) {
        phoneRef.current?.scrollIntoView({ behavior: "smooth" });
      } else if (errors.contractChoice) {
        contractChoiceRef.current?.scrollIntoView({ behavior: "smooth" });
      } else if (errors.paymentStartYear) {
        paymentStartYearRef.current?.scrollIntoView({ behavior: "smooth" });
      } else if (errors.paymentStartDay) {
        paymentStartDayRef.current?.scrollIntoView({ behavior: "smooth" });
      } else if (errors.leaveYear) {
        leaveYearRef.current?.scrollIntoView({ behavior: "smooth" });
      } else if (errors.leavePeriod) {
        leavePeriodRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handlePhoneChange = (value) => {
    set_Form_Data((prevData) => {
      const updatedData = { ...prevData, phoneNumber: value };
      if (firstFormValidation) {
        setFormErrors(validateFormData(updatedData, acknowledgeSignature));
      }
      return updatedData;
    });
  };

  const handleAcknowledgeSignatureChange = (e) => {
    const newVal = e.target.checked;
    setAcknowledgeSignature(newVal);
    if (firstFormValidation) {
      const updatedErrors = validateFormData(form_Data, newVal);
      setFormErrors(updatedErrors);
    }
  };

  const handleEmailVerified = useCallback((email) => {
    setEmailVerified(true);
    set_Form_Data((prev) => ({
      ...prev,
      emailVerified: true,
      emailToVerify: email,
    }));
    setFormErrors((prev) => ({
      ...prev,
      emailVerification: undefined,
    }));
  }, []);


  const inputStyle = (hasError, isValid) => ({
    width: "100%",
    backgroundColor: "#ffffff",
    border: hasError ? "1.5px solid #ba1a1a" : isValid ? "1.5px solid #198754" : "none",
    borderRadius: "0.5rem",
    padding: "0.75rem",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    outline: "none",
    fontSize: "0.95rem",
    boxSizing: "border-box",
    fontFamily: "Inter, sans-serif",
  });

  const labelStyle = {
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#414751",
    display: "block",
    marginBottom: "0.5rem",
  };

  const errorStyle = {
    color: "#ba1a1a",
    fontSize: "0.8rem",
    marginTop: "0.25rem",
  };

  const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "2rem",
  };

  const sectionBadgeStyle = {
    backgroundColor: "#00447f",
    color: "#ffffff",
    width: 32,
    height: 32,
    borderRadius: "9999px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    flexShrink: 0,
    fontSize: "0.95rem",
  };

  const sectionTitleStyle = {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "#00447f",
    margin: 0,
    fontFamily: "Public Sans, sans-serif",
  };

  const modernSelectStyles = (hasError, isValid) => ({
    control: (base, state) => ({
      ...base,
      borderColor: hasError ? "#ba1a1a" : isValid ? "#198754" : "transparent",
      backgroundColor: "#ffffff",
      borderRadius: "0.5rem",
      boxShadow: state.isFocused ? "0 0 0 2px #00447f" : "0 1px 3px rgba(0,0,0,0.08)",
      minHeight: "48px",
      "&:hover": { borderColor: "#00447f" },
    }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  });

  if (submittedData) {
    return (
      <FormConfirmation
        headerTitle="Congé à traitement différé"
        headerSub="Demande de congé à traitement différé."
        dateSent={submittedData.dateSent}
        details={[
          { label: "Employé(e)", value: submittedData.name },
          { label: "Matricule", value: submittedData.matricule },
          { label: "Établissement", value: submittedData.establishment },
          { label: "Formule choisie", value: submittedData.contrat },
          { label: "Année de congé", value: submittedData.anneeConge, icon: "calendar_today" },
        ]}
      />
    );
  }

  return (
    <div style={{ backgroundColor: "#f9f9ff", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #00447f 0%, #0066cc 100%)", padding: "1.25rem 1.5rem", display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap", boxShadow: "0 2px 12px rgba(0,68,127,0.18)" }}>
        <Link to="/">
          <img src="logo.jpg" alt="logo" style={{ maxHeight: "56px", width: "auto", borderRadius: "0.5rem", backgroundColor: "rgba(255,255,255,0.95)", padding: "0.25rem", flexShrink: 0 }} />
        </Link>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ color: "#ffffff", fontSize: "1.2rem", fontWeight: 700, margin: 0, lineHeight: 1.2 }}>Congé à traitement différé</h1>
          <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.85rem", margin: "0.25rem 0 0 0" }}>Demande de congé à traitement différé.</p>
        </div>
      </div>

      {/* Page content */}
      <div style={{ maxWidth: "64rem", margin: "0 auto", padding: "2rem 1.5rem 8rem" }}>

        {/* Notes banner */}
        <div
          style={{
            backgroundColor: "rgba(212, 227, 255, 0.35)",
            borderRadius: "0.75rem",
            padding: "1.5rem",
            marginBottom: "3rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.75rem" }}>
            <span className="material-symbols-outlined" style={{ color: "#005ca8", display: "inline-block", lineHeight: 1, flexShrink: 0 }}>
              info
            </span>
            <h4 style={{ fontWeight: 700, color: "#00447f", margin: 0, fontFamily: "Public Sans, sans-serif" }}>
              Remarques
            </h4>
          </div>
          <ul style={{ paddingLeft: "1.75rem", margin: 0, display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <li style={{ color: "#414751", fontSize: "0.9rem" }}>
              Veuillez respecter la date limite pour transmettre votre demande :
              <ul style={{ paddingLeft: "1.25rem", marginTop: "0.25rem" }}>
                <li>1<sup>er</sup> juillet pour débuter un paiement au 1<sup>er</sup> jour de l'année scolaire;</li>
                <li>5 janvier pour débuter un paiement à la 101<sup>e</sup> journée de l'année scolaire.</li>
              </ul>
            </li>
            <li style={{ color: "#414751", fontSize: "0.9rem" }}>
              Si vous avez des questions, n'hésitez pas à communiquer avec Valérie Caissie :{" "}
              <a href="mailto:valeriec@sevf.ca" style={{ color: "#00447f" }}>valeriec@sevf.ca</a>
            </li>
            <li style={{ color: "#414751", fontSize: "0.9rem" }}>
              Suite à la transmission de votre demande, le Centre de service en fera l'analyse et vous rendra une décision.
            </li>
          </ul>
        </div>

        <form
          noValidate
          onSubmit={handleSubmit}
          id="form-container"
          style={{ display: "flex", flexDirection: "column", gap: "2.5rem" }}
        >
          {/* ── Section 1 : Informations Personnelles ── */}
          <section style={{ backgroundColor: "#f2f3fe", borderRadius: "0.75rem", padding: "2rem" }}>
            <div style={sectionHeaderStyle}>
              <span style={sectionBadgeStyle}>1</span>
              <h2 style={sectionTitleStyle}>Informations Personnelles</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: "1.5rem" }}>
              <div ref={firstNameRef}>
                <label htmlFor="firstName" style={labelStyle}>Prénom</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={form_Data.firstName}
                  onChange={handleChange}
                  required
                  placeholder="Entrez votre prénom"
                  style={inputStyle(formErrors.firstName, formValidated && !formErrors.firstName)}
                />
                {formErrors.firstName && <p style={errorStyle}>{formErrors.firstName}</p>}
              </div>

              <div ref={lastNameRef}>
                <label htmlFor="lastName" style={labelStyle}>Nom de famille</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={form_Data.lastName}
                  onChange={handleChange}
                  required
                  placeholder="Entrez votre nom"
                  style={inputStyle(formErrors.lastName, formValidated && !formErrors.lastName)}
                />
                {formErrors.lastName && <p style={errorStyle}>{formErrors.lastName}</p>}
              </div>

              <div ref={establishmentRef} className="md:col-span-2">
                <label htmlFor="establishment" style={labelStyle}>Nom de l'école ou du centre</label>
                <Select
                  id="establishment"
                  name="establishment"
                  options={schools
                    .sort((a, b) => a.number - b.number)
                    .map((school) => ({
                      value: `${school.number} ${school.name}`,
                      label: `${school.number} ${school.name}`,
                    }))}
                  value={
                    form_Data.establishment
                      ? { value: form_Data.establishment, label: form_Data.establishment }
                      : null
                  }
                  onChange={(selectedOption) => {
                    const value = selectedOption ? selectedOption.value : "";
                    set_Form_Data((prevData) => {
                      const updatedData = { ...prevData, establishment: value };
                      if (firstFormValidation) {
                        setFormErrors(validateFormData(updatedData, acknowledgeSignature));
                      }
                      return updatedData;
                    });
                  }}
                  placeholder="Rechercher une école..."
                  classNamePrefix="react-select"
                  styles={modernSelectStyles(formErrors.establishment, formValidated && !formErrors.establishment)}
                />
                {formErrors.establishment && <p style={errorStyle}>{formErrors.establishment}</p>}
              </div>

              <div ref={matriculeRef}>
                <label htmlFor="matricule" style={labelStyle}>Numéro de matricule</label>
                <input
                  type="number"
                  id="matricule"
                  name="matricule"
                  value={form_Data.matricule}
                  onChange={handleChange}
                  onWheel={(e) => e.target.blur()}
                  required
                  placeholder="123456"
                  style={inputStyle(formErrors.matricule, formValidated && !formErrors.matricule)}
                />
                {formErrors.matricule && <p style={errorStyle}>{formErrors.matricule}</p>}
              </div>

              <div ref={phoneRef}>
                <label
                  style={{
                    ...labelStyle,
                    color: formErrors.phoneNumber ? "#ba1a1a" : formValidated && !formErrors.phoneNumber ? "#198754" : "#414751",
                  }}
                >
                  Numéro de téléphone
                </label>
                <div
                  style={{
                    border: formErrors.phoneNumber
                      ? "1.5px solid #ba1a1a"
                      : formValidated && !formErrors.phoneNumber
                        ? "1.5px solid #198754"
                        : "none",
                    backgroundColor: "#ffffff",
                    borderRadius: "0.5rem",
                    padding: "0.5rem 0.75rem",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  }}
                >
                  <PhoneInput
                    defaultCountry="CA"
                    placeholder="XXX-XXX-XXXX"
                    value={form_Data.phoneNumber}
                    onChange={handlePhoneChange}
                    name="phoneNumber"
                    required
                  />
                </div>
                {formErrors.phoneNumber && <p style={errorStyle}>{formErrors.phoneNumber}</p>}
              </div>
            </div>
          </section>

          {/* ── Section 2 : Contrat ── */}
          <section
            ref={contractChoiceRef}
            style={{
              backgroundColor: "#f2f3fe",
              borderRadius: "0.75rem",
              padding: "2rem",
              border: formErrors.contractChoice ? "1.5px solid #ba1a1a" : "none",
            }}
          >
            <div style={sectionHeaderStyle}>
              <span style={sectionBadgeStyle}>2</span>
              <h2 style={sectionTitleStyle}>Contrat</h2>
            </div>
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "0.5rem",
                padding: "0.875rem 1rem",
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                marginBottom: "1.25rem",
                fontSize: "0.875rem",
                color: "#414751",
              }}
            >
              <strong>Exemple :</strong> « ½ année / 2 ans = 75,00 % » signifie que vous recevrez 75 % de votre salaire pendant 2 ans. Ceci vous permettra de continuer à être payé 75 % de votre salaire durant votre congé d'une demi-année qui pourra débuter lorsque vous aurez complété la moitié du remboursement (donc dans ce cas-ci, après 1 an).
            </div>
            <p style={{ fontSize: "0.875rem", fontWeight: 600, color: formErrors.contractChoice ? "#ba1a1a" : "#414751", marginBottom: "0.75rem" }}>
              Veuillez cocher votre choix de contrat :
            </p>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {contractOptions.map((option) => (
                <RadioButton
                  key={option.id}
                  id={option.id}
                  name="contractChoice"
                  label={`${option.label} de votre salaire`}
                  value={option.id}
                  checked={form_Data.contractChoice === option.id}
                  onChange={handleContractChange}
                />
              ))}
            </div>
            {formErrors.contractChoice && <p style={{ ...errorStyle, marginTop: "0.75rem" }}>{formErrors.contractChoice}</p>}
          </section>

          {/* ── Section 3 : Paiement ── */}
          <AnimatePresence>
            {form_Data.contractChoice && (
              <motion.section
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                ref={paymentStartYearRef}
                style={{
                  backgroundColor: "#f2f3fe",
                  borderRadius: "0.75rem",
                  padding: "2rem",
                  border: formErrors.paymentStartYear || formErrors.paymentStartDay ? "1.5px solid #ba1a1a" : "none",
                }}
              >
                <div style={sectionHeaderStyle}>
                  <span style={sectionBadgeStyle}>3</span>
                  <h2 style={sectionTitleStyle}>Paiement</h2>
                </div>
                <p style={{ fontSize: "0.875rem", color: "#414751", marginBottom: "1.5rem" }}>
                  Deux choix possibles : au début de l'année scolaire ou à la 101<sup>e</sup> journée.
                </p>

                <div style={{ maxWidth: "24rem", marginBottom: "1.5rem" }}>
                  <label htmlFor="paymentStartYear" style={labelStyle}>Année scolaire</label>
                  <Select
                    id="paymentStartYear"
                    name="paymentStartYear"
                    options={schoolYearOptions}
                    value={
                      form_Data.paymentStartYear
                        ? { value: form_Data.paymentStartYear, label: form_Data.paymentStartYear }
                        : null
                    }
                    onChange={(selectedOption) => {
                      const value = selectedOption ? selectedOption.value : "";
                      set_Form_Data((prevData) => {
                        const updatedData = { ...prevData, paymentStartYear: value };
                        if (firstFormValidation) {
                          setFormErrors(validateFormData(updatedData, acknowledgeSignature));
                        }
                        return updatedData;
                      });
                    }}
                    placeholder="Sélectionner une année..."
                    classNamePrefix="react-select"
                    styles={modernSelectStyles(formErrors.paymentStartYear, formValidated && !formErrors.paymentStartYear)}
                  />
                  {formErrors.paymentStartYear && <p style={errorStyle}>{formErrors.paymentStartYear}</p>}
                </div>

                <div ref={paymentStartDayRef}>
                  <label style={{ ...labelStyle, marginBottom: "0.75rem" }}>Moment pour débuter le paiement</label>
                  <RadioButton
                    id="premiereJournee"
                    name="paymentStartDay"
                    label={<>1<sup>re</sup> journée de l'année scolaire</>}
                    value="premiereJournee"
                    checked={form_Data.paymentStartDay === "premiereJournee"}
                    onChange={handleChange}
                  />
                  <RadioButton
                    id="101eJournee"
                    name="paymentStartDay"
                    label={<>101<sup>e</sup> journée de l'année scolaire</>}
                    value="101eJournee"
                    checked={form_Data.paymentStartDay === "101eJournee"}
                    onChange={handleChange}
                  />
                  {formErrors.paymentStartDay && <p style={{ ...errorStyle, marginTop: "0.5rem" }}>{formErrors.paymentStartDay}</p>}
                </div>
              </motion.section>
            )}
          </AnimatePresence>

          {/* ── Section 4 : Prise du Congé ── */}
          <AnimatePresence>
            {form_Data.paymentStartYear && form_Data.paymentStartDay && (
              <motion.section
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                ref={leaveYearRef}
                style={{
                  backgroundColor: "#f2f3fe",
                  borderRadius: "0.75rem",
                  padding: "2rem",
                  border: formErrors.leaveYear || formErrors.leavePeriod ? "1.5px solid #ba1a1a" : "none",
                }}
              >
                <div style={sectionHeaderStyle}>
                  <span style={sectionBadgeStyle}>4</span>
                  <h2 style={sectionTitleStyle}>Prise du congé</h2>
                </div>
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "0.5rem",
                    padding: "0.875rem 1rem",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    marginBottom: "1.25rem",
                    fontSize: "0.875rem",
                    color: "#414751",
                  }}
                >
                  <strong>Important :</strong> Pour prendre le congé, il faut avoir payé la moitié du contrat et ne pas dépasser la date de fin du paiement.
                </div>

                <div style={{ maxWidth: "24rem", marginBottom: "1.5rem" }}>
                  <label htmlFor="leaveYear" style={labelStyle}>Année scolaire du congé</label>
                  <Select
                    id="leaveYear"
                    name="leaveYear"
                    options={schoolYearOptions}
                    value={
                      form_Data.leaveYear
                        ? { value: form_Data.leaveYear, label: form_Data.leaveYear }
                        : null
                    }
                    onChange={(selectedOption) => {
                      const value = selectedOption ? selectedOption.value : "";
                      set_Form_Data((prevData) => {
                        const updatedData = { ...prevData, leaveYear: value };
                        if (firstFormValidation) {
                          setFormErrors(validateFormData(updatedData, acknowledgeSignature));
                        }
                        return updatedData;
                      });
                    }}
                    placeholder="Sélectionner une année..."
                    classNamePrefix="react-select"
                    styles={modernSelectStyles(formErrors.leaveYear, formValidated && !formErrors.leaveYear)}
                  />
                  {formErrors.leaveYear && <p style={errorStyle}>{formErrors.leaveYear}</p>}
                </div>

                {form_Data.leaveYear && isHalfYearContract() === true && (
                  <div ref={leavePeriodRef}>
                    <label style={{ ...labelStyle, color: formErrors.leavePeriod ? "#ba1a1a" : "#414751", marginBottom: "0.75rem" }}>
                      Période du congé
                    </label>
                    <RadioButton
                      id="premiers100Jours"
                      name="leavePeriod"
                      label="Les 100 premiers jours de l'année scolaire"
                      value="premiers100Jours"
                      checked={form_Data.leavePeriod === "premiers100Jours"}
                      onChange={handleChange}
                    />
                    <RadioButton
                      id="derniers100Jours"
                      name="leavePeriod"
                      label="Les 100 derniers jours de l'année scolaire"
                      value="derniers100Jours"
                      checked={form_Data.leavePeriod === "derniers100Jours"}
                      onChange={handleChange}
                    />
                    {formErrors.leavePeriod && <p style={{ ...errorStyle, marginTop: "0.5rem" }}>{formErrors.leavePeriod}</p>}
                  </div>
                )}
              </motion.section>
            )}
          </AnimatePresence>

          {/* ── Signature ── */}
          <AnimatePresence>
            {(form_Data.leavePeriod || (form_Data.leaveYear && isHalfYearContract() === false)) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div
                  style={{
                    backgroundColor: "#f2f3fe",
                    borderRadius: "0.75rem",
                    padding: "1.5rem",
                    border: formErrors.acknowledgeSignature ? "1.5px solid #ba1a1a" : "none",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
                    <input
                      type="checkbox"
                      id="acknowledgeSignature"
                      checked={acknowledgeSignature}
                      onChange={handleAcknowledgeSignatureChange}
                      style={{ accentColor: "#00447f", width: 18, height: 18, flexShrink: 0, marginTop: 3, cursor: "pointer" }}
                    />
                    <label htmlFor="acknowledgeSignature" style={{ fontSize: "0.875rem", color: "#414751", lineHeight: 1.6, cursor: "pointer" }}>
                      En cochant cette case, je confirme que les informations fournies dans ce formulaire sont exactes et conformes à ma situation. Je fais une demande officielle, laquelle sera transmise aux services des ressources humaines du Centre de services scolaire du Chemin-du-Roy pour traitement, et une copie sera acheminée au Syndicat de l'enseignement des Vieilles-Forges. <strong>Je comprends que le fait de cocher cette case fait office de signature officielle.</strong>
                    </label>
                  </div>
                  {formErrors.acknowledgeSignature && <p style={{ ...errorStyle, marginTop: "0.5rem" }}>{formErrors.acknowledgeSignature}</p>}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Email Verification ── */}
          <AnimatePresence>
            {acknowledgeSignature && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <EmailVerification
                  apiUrl={apiUrl}
                  domain={
                    apiUrl?.includes("ngrok")
                      ? ""
                      : apiUrl?.includes("localhost")
                        ? "@gmail.com"
                        : "@csscdr.gouv.qc.ca"
                  }
                  error={formErrors.emailVerification}
                  onVerified={handleEmailVerified}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <button
            type="submit"
            style={{
              backgroundColor: "#00447f",
              color: "#ffffff",
              border: "none",
              borderRadius: "0.5rem",
              padding: "0.875rem 2.5rem",
              fontSize: "1rem",
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "Public Sans, sans-serif",
              alignSelf: "flex-start",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#005ca8")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#00447f")}
          >
            Envoyer
          </button>
          {submitError && (
            <div style={{
              marginTop: "1rem",
              padding: "1rem 1.25rem",
              backgroundColor: "#fff0f0",
              border: "1.5px solid #ba1a1a",
              borderRadius: "0.625rem",
              color: "#ba1a1a",
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}>
              {submitError}
            </div>
          )}
          {loading && (
            <div className="loading-overlay">
              <img src="/loading-logo.gif" alt="Loading..." />
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default CongeTraitementDiffere;
